import React, { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { connect } from 'scrivito'

import { FilterToast } from './FilterToast'
import { removeListObjectFilterConditions } from './listObjectFilter'
import { SaveAsFilterModal } from './SaveAsFilterModal'
import { Icon } from '@/Atomic/Atoms/Icon'
import { Button } from '@/Atomic/Molecules/Button'
import {
  FilterBuilder,
  useFilterBuilder,
  pruneBlankConditions,
  SavedFilterStructure,
} from '@/Atomic/Organisms/FilterBuilder'
import { Modal } from '@/Atomic/Organisms/Modal'
import { useActiveFilter } from '@/hooks/useActiveFilter'
import { useEntitySchema } from '@/hooks/useEntitySchema'
import './AdvancedSearchModal.scss'

export interface AdvancedSearchModalProps {
  isOpen: boolean
  /**
   * `true` opens the builder pre-filled with the filter applied in the URL
   * (Edit); `false` opens it blank (the "Advanced filter" button — a new filter).
   */
  seeded?: boolean
  onClose: () => void
  onSearch: (filter: SavedFilterStructure) => void
  onClearAll?: () => void
  entityName: string
  renderInline?: boolean
}

export const AdvancedSearchModal: React.FC<AdvancedSearchModalProps> = connect(
  ({
    isOpen,
    seeded = false,
    onClose,
    onSearch,
    onClearAll,
    entityName,
    renderInline = false,
  }: AdvancedSearchModalProps) => {
    const { t } = useTranslation()
    const { attributes: baseSchemaAttributes, relations: schemaRelations } =
      useEntitySchema(entityName)
    const [isSaveAsFilterModalOpen, setIsSaveAsFilterModalOpen] =
      useState(false)
    const [isFilterSaved, setIsFilterSaved] = useState(false)

    // The filter to seed from comes straight from the URL — same source the list
    // itself reads. Saving is owned here too, so no callbacks are threaded in.
    const { draft, createFilter, selectSavedFilter } =
      useActiveFilter(entityName)
    // Edit is only offered on the "Unsaved" row, so seed from the draft. Do NOT
    // fall back to `activeStructure`: that would load the *saved* filter's
    // conditions whenever one is selected alongside a retained draft.
    const initialFilter = seeded ? draft : undefined

    const {
      filter,
      setFilter,
      validateFilter,
      toSavedFormat,
      createEmptyFilter,
      errors,
      clearError,
      loadFromSavedFilter,
    } = useFilterBuilder({
      entityName,
      initialFilter,
      baseSchemaAttributes,
      schemaRelations,
    })

    // Seed the builder when the modal opens: from the URL filter when opened via
    // Edit, empty when opened via the "Advanced filter" button (a new filter).
    //
    // FRAGILE — this effect calls setFilter, so any dep that changes identity on
    // every render re-fires it on its own setState and hangs the tab. Two deps
    // are not inherently stable:
    //   - `initialFilter`, decoded from `?query=`. Held stable only by the memo
    //     in `useActiveFilter`; an unmemoized filter passed in here reintroduces
    //     the loop. This was a real bug — Edit on the "Unsaved" row froze.
    //   - `loadFromSavedFilter`, a useCallback keyed on `baseSchemaAttributes`,
    //     which is useState in `useEntitySchema` and lands after an async load.
    //     Benign today (re-seeding is idempotent), but unguarded.
    //
    // The proper fix is to delete this effect: remount the body on open with
    // `key={seeded ? queryParam : 'blank'}` and seed from useFilterBuilder's
    // useState initializer, which already duplicates this logic. Then neither
    // dep's identity matters. Needs `isFilterSaved` / `isSaveAsFilterModalOpen`
    // to move or reset on remount, so it is its own change.
    useEffect(() => {
      if (!isOpen) return

      // Reset the saved state when modal opens
      setIsFilterSaved(false)

      if (initialFilter) {
        loadFromSavedFilter(initialFilter)
      } else {
        setFilter(createEmptyFilter())
      }
    }, [
      isOpen,
      initialFilter,
      loadFromSavedFilter,
      setFilter,
      createEmptyFilter,
    ])

    const handleSearch = () => {
      // Emptied builder → the intent is "remove the filter", not a broken row.
      // Drop the advanced filter instead of validating the blank condition.
      if (!hasActiveConditions) {
        onClearAll?.()
        onClose()
        return
      }

      const { isValid } = validateFilter()

      if (!isValid) {
        return
      }

      const transformedFilter = removeListObjectFilterConditions(
        entityName,
        toSavedFormat(),
      )

      onSearch(transformedFilter)
      onClose()

      // Show toast notification
      toast(<FilterToast variant="applied" onClearFilter={onClearAll} />)
    }

    const handleSaveAsFilterClick = () => {
      const { isValid } = validateFilter()

      if (!isValid) {
        return
      }

      setIsSaveAsFilterModalOpen(true)
    }

    const handleSaveAsFilter = async (filterName: string) => {
      const transformedFilter = removeListObjectFilterConditions(
        entityName,
        toSavedFormat(),
      )
      // Wait for the filter to be created and selected before closing. The draft
      // was promoted to a named filter, so `selectSavedFilter` drops `?query=`.
      const newFilter = await createFilter(filterName, transformedFilter)
      selectSavedFilter(newFilter.id)
      // Close both modals after filter is saved
      setIsSaveAsFilterModalOpen(false)
      onClose()

      // Show toast notification for saved filter
      toast(<FilterToast variant="saved" onClearFilter={onClearAll} />)
    }

    const handleCancel = () => {
      onClose()
    }

    const hasActiveConditions = useMemo(
      () =>
        filter.groups.some((group) =>
          group.conditions.some((cond) => cond.field !== ''),
        ),
      [filter],
    )

    // Dirty vs what was opened. Compare with blank conditions stripped on both
    // sides so an emptied builder still counts as a change (clearing a seeded
    // filter is a valid "apply no filter"), and an untouched seeded filter reads
    // as clean. `initialFilter` undefined (new filter) → baseline is empty.
    const isDirty = useMemo(
      () =>
        JSON.stringify(pruneBlankConditions(toSavedFormat())) !==
        JSON.stringify(pruneBlankConditions(initialFilter)),
      [toSavedFormat, initialFilter],
    )

    // Only empty the builder — do not touch the URL or close. The user then
    // commits via Apply or discards via Cancel.
    const handleClearAll = () => {
      setFilter(createEmptyFilter())
    }

    return (
      <>
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          className="advanced-search-modal"
          closeOnOutsideClick={!isSaveAsFilterModalOpen}
          disablePortal={renderInline}
        >
          <Modal.Header>
            <h3 className="o-modal-title">
              {t('advancedSearch.advancedFilter')}
            </h3>
            {hasActiveConditions && (
              <button
                className="advanced-search-modal__clear-all"
                onClick={handleClearAll}
              >
                {t('advancedSearch.clearAllCriteria')}
              </button>
            )}
          </Modal.Header>
          <Modal.Body>
            <FilterBuilder
              filter={filter}
              onChange={setFilter}
              entityName={entityName}
              errors={errors}
              onClearError={clearError}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outlined" onClick={handleCancel}>
              {t('common.cancel')}
            </Button>
            <div className="footer-right">
              <Button
                variant="outlined"
                onClick={handleSearch}
                disabled={!isDirty}
              >
                {t('common.apply')}
              </Button>
              {isFilterSaved ? (
                <Button variant="primary" className="filter-saved-btn" disabled>
                  <Icon icon="check" size="sm" />
                  {t('advancedSearch.filterSaved')}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleSaveAsFilterClick}
                  disabled={!hasActiveConditions}
                >
                  {t('advancedSearch.saveAndApply')}
                </Button>
              )}
            </div>
          </Modal.Footer>
        </Modal>
        <SaveAsFilterModal
          isOpen={isSaveAsFilterModalOpen}
          onClose={() => setIsSaveAsFilterModalOpen(false)}
          onSave={handleSaveAsFilter}
          title={t('advancedSearch.saveFilter')}
        />
      </>
    )
  },
)
